// Phase 7: the directors' dashboard aggregates, leg times, predicted delivery dates and the season
// board (13 §13.3-13.4, 15 §15.1). Everything here is written by the server; clients only read.
//
// FOR THE OFFICE SCREENS. Names in this file:
//
//   Dashboard documents (server-written, one per period and scope, read with `dashboard.view`):
//     `dashboards/{period}__{scope}`  ->  `dashboardDocSchema` / `DashboardDoc`
//       period  `day` | `week` | `month`            (the CURRENT day / ISO week (Mon-Sun) / month)
//       scope   `group`                              id `week__group`          needs access `all`
//               `entity__{SECTOR}`                   id `week__entity__KSA`    needs that country
//               `branch__{BRANCH}`                   id `week__branch__RUH`    needs that branch
//       Build the id with `dashboardId(period, scopeKind, scopeId)`; list the scopes with a query on
//       the fields the rules can prove: `where('scopeKind','==','entity').where('scopeId','==','KSA')`
//       (a list query MUST filter on `scopeKind` and `scopeId`, or `where('period','==','week')` is
//       not enough for the rules).
//       .speed          `speedSchema`      door-to-door by route/service/region, on-time %, leg breakdown, boxes by stage
//       .money          `moneySchema`      every amount is an `Amount` = {minor (scope currency), aedMinor}; the group is in AED
//       .customers      `customersSchema`
//       .health         `healthSchema`     operations health (exceptions, missing/damaged per 1,000, pickups, day closes, bank recon)
//       .attention      `attentionSchema`  row 1 counts that are cheap to keep here
//       .comparison     `comparisonSchema` this period against the one before, metric by metric
//       .limits         `dashboardLimitsSchema`  what the job read; `truncated` means the numbers may be low
//     "All money in AED with a switch": show `amount.aedMinor` (always present for the group) or
//     `amount.minor` with `money.currency` (the scope's own currency). `aedMinor` is null when no FX
//     rate converts the scope's currency (see `money.missingFx`).
//     Every number "links to the list behind it": use `range.from`/`range.to` (business dates,
//     inclusive) and the scope to open the matching list (Shipments, Exceptions, Customers...).
//     Written by the hourly `dashboardAggregates` job; a dev runs it with `runControlTowerNow {jobs: ['dashboards']}`.
//
//   Leg times (server-written; read in scope like a shipment):
//     `shipmentLegs/{trackingNo}`  ->  `shipmentLegsSchema`  the eight legs of one shipment, door-to-door days
//     `legStats/{route}__{service}` -> `legStatsSchema`      history the predictions are made from (server only)
//
//   Predicted delivery (13 §13.3): `shipments/{no}.predictedWindow` and the customer's
//   `customerShipments/{uid}__{no}.predicted` ({from, to}); the public tracking answer's `predicted`
//   gets `basis: 'history'` once a route has enough history. Shape: `predictedWindowSchema`.
//
//   Season board (13 §13.4): `seasonBoards/{seasonId}` -> `seasonBoardSchema`. Read with `dashboard.view` and access `all`.
//
// Money in these documents is integer minor units. Percentages are 0-100 numbers with one decimal,
// days have two decimals. A metric with no data is `null`, never 0.
import { z } from 'zod';
import {
  BOX_STAGES,
  DASHBOARD_PERIODS,
  LEG_KEYS,
  LEG_LABELS,
  PREDICTION_STAGES,
  SEASON_RISKS,
  type BoxStage,
  type DashboardPeriod,
  type LegKey,
  type PredictionStage,
  type SeasonRisk,
} from '@ecobox/domain';
import { branchCodeSchema, sectorCodeSchema } from './config';

// The vocabularies live in the domain (the rules that use them are there); they are re-exported so
// a screen can take every name from this package.
export { BOX_STAGES, DASHBOARD_PERIODS, LEG_KEYS, LEG_LABELS, PREDICTION_STAGES, SEASON_RISKS };
export type { BoxStage, DashboardPeriod, LegKey, PredictionStage, SeasonRisk };

const int = z.number().int().nonnegative();
const sint = z.number().int();
const days = z.number().nonnegative();
const pct = z.number().min(0).max(100);
const iso = z.string();
const date = z.iso.date();

// ---- Ids ------------------------------------------------------------------------------------

export const DASHBOARD_SCOPE_KINDS = ['group', 'entity', 'branch'] as const;
export type DashboardScopeKind = (typeof DASHBOARD_SCOPE_KINDS)[number];

/** `week__group`, `week__entity__KSA`, `month__branch__RUH`. */
export function dashboardId(
  period: DashboardPeriod,
  scopeKind: DashboardScopeKind,
  scopeId?: string,
): string {
  return scopeKind === 'group' ? `${period}__group` : `${period}__${scopeKind}__${scopeId ?? ''}`;
}

/** The inverse of `dashboardId`, or undefined for an id that is not a dashboard's. */
export function parseDashboardId(
  id: string,
): { period: DashboardPeriod; scopeKind: DashboardScopeKind; scopeId: string } | undefined {
  const [period, kind, scopeId, ...rest] = id.split('__');
  if (!DASHBOARD_PERIODS.includes(period as DashboardPeriod) || rest.length > 0) return undefined;
  if (kind === 'group' && scopeId === undefined) {
    return { period: period as DashboardPeriod, scopeKind: 'group', scopeId: 'group' };
  }
  if ((kind === 'entity' || kind === 'branch') && scopeId) {
    return { period: period as DashboardPeriod, scopeKind: kind, scopeId };
  }
  return undefined;
}

// ---- Legs -----------------------------------------------------------------------------------

const legDays = days.nullable();
export const legDaysSchema = z
  .object({
    originDwell: legDays,
    consolidation: legDays,
    transit: legDays,
    portDwell: legDays,
    customs: legDays,
    devanning: legDays,
    destinationDwell: legDays,
    delivery: legDays,
  })
  .strict();
export type LegDays = z.infer<typeof legDaysSchema>;

/** The moments the legs are cut at; null until the shipment gets there. Each is the LAST box's. */
export const legPointsSchema = z
  .object({
    collectedAt: iso,
    loadedAt: iso.nullable(),
    departedAt: iso.nullable(),
    arrivedAt: iso.nullable(),
    customsLodgedAt: iso.nullable(),
    customsReleasedAt: iso.nullable(),
    pulledOutAt: iso.nullable(),
    atHubAt: iso.nullable(),
    handedOverAt: iso.nullable(),
    deliveredAt: iso.nullable(),
  })
  .strict();
export type LegPoints = z.infer<typeof legPointsSchema>;

/** `shipmentLegs/{trackingNo}`: written by a trigger as legs complete, final when delivered. */
export const shipmentLegsSchema = z
  .object({
    version: z.literal(1),
    trackingNo: z.string(),
    /** For the rules: where the shipment was taken in. */
    branchId: branchCodeSchema,
    sectorId: sectorCodeSchema,
    /** Where it is served from (the destination hub) and its country, when known. */
    servingBranchId: branchCodeSchema.nullable(),
    destinationSectorId: z.string().nullable(),
    /** `{origin sector}-{route code, else destination country}`, e.g. `KSA-MNL`. */
    route: z.string(),
    service: z.enum(['sea', 'air', 'prime']),
    cityId: z.string(),
    cityName: z.string(),
    region: z.string(),
    partnerId: z.string().nullable(),
    deliveryMode: z.enum(['own', 'partner']).nullable(),
    seasonTags: z.array(z.string()),
    boxCount: int,
    containerIds: z.array(z.string()),
    points: legPointsSchema,
    legs: legDaysSchema,
    /** Legs that could not be worked out yet (their ends are not both known). */
    missing: z.array(z.enum(LEG_KEYS)),
    /** Collected to delivered, in days; null until delivered. */
    doorToDoorDays: days.nullable(),
    collectedAt: iso,
    /** Delivery time, null until delivered. The dashboards range-query on it. */
    deliveredAt: iso.nullable(),
    complete: z.boolean(),
    /** The door-to-door target in force when this was written, and where it came from. */
    targetDays: days,
    targetSource: z.enum(['sla', 'default']),
    /** The destination city's delivery target (hub to door), when it has one. */
    lastMileTargetDays: days.nullable(),
    /** Door-to-door within target; null until delivered. */
    onTime: z.boolean().nullable(),
    /** Last-mile days (handed over to delivered) within the city target; null when unknown. */
    lastMileOnTime: z.boolean().nullable(),
    computedAt: iso,
  })
  .strict();
export type ShipmentLegsDoc = z.infer<typeof shipmentLegsSchema>;

/** `legStats/{route}__{service}`: remaining days to delivery, per stage, of recently delivered shipments. */
export const legStatsSchema = z
  .object({
    id: z.string(),
    route: z.string(),
    service: z.enum(['sea', 'air', 'prime']),
    /** Delivered shipments these samples come from. */
    shipments: int,
    /** Days from reaching the stage to delivery, ascending, at most 300 per stage. */
    remaining: z.partialRecord(
      z.enum(PREDICTION_STAGES),
      z.array(z.number().nonnegative()).max(300),
    ),
    /** Door-to-door days of the same shipments, ascending. */
    doorToDoor: z.array(z.number().nonnegative()).max(300),
    updatedAt: iso,
  })
  .strict();
export type LegStatsDoc = z.infer<typeof legStatsSchema>;

/** `shipments/{no}.predictedWindow` (13 §13.3): a median and a spread, or the SLA target while history is thin. */
export const predictedWindowSchema = z
  .object({
    from: date,
    to: date,
    median: date,
    basis: z.enum(['history', 'target']),
    /** Past shipments the range comes from (0 for `target`). */
    samples: int,
    stage: z.enum(PREDICTION_STAGES),
    computedAt: iso,
  })
  .strict();
export type PredictedWindow = z.infer<typeof predictedWindowSchema>;

// ---- Dashboard building blocks --------------------------------------------------------------

/** A sum of money: in the scope's currency, and in AED (null when no rate converts it). */
export const amountSchema = z.object({ minor: sint, aedMinor: sint.nullable() }).strict();
export type Amount = z.infer<typeof amountSchema>;

const labelled = { key: z.string(), label: z.string() };

export const d2dStatSchema = z
  .object({
    ...labelled,
    count: int,
    medianDays: days,
    p90Days: days,
    avgDays: days,
  })
  .strict();
export type D2dStat = z.infer<typeof d2dStatSchema>;

export const onTimeStatSchema = z
  .object({ ...labelled, total: int, onTime: int, pct: pct.nullable() })
  .strict();
export type OnTimeStat = z.infer<typeof onTimeStatSchema>;

/** The leg breakdown of one route: the average days per leg over its delivered shipments. */
export const routeLegsSchema = z
  .object({
    ...labelled,
    service: z.enum(['sea', 'air', 'prime', 'all']),
    count: int,
    avgDays: legDaysSchema,
    /** Sum of the average legs: how long the journey takes on average, leg by leg. */
    totalDays: days,
  })
  .strict();
export type RouteLegs = z.infer<typeof routeLegsSchema>;

export const stageStatSchema = z
  .object({
    stage: z.enum(BOX_STAGES),
    count: int,
    /** Average and oldest time in the stage, from the last event, in days. */
    avgAgeDays: days,
    oldestAgeDays: days,
  })
  .strict();
export type StageStat = z.infer<typeof stageStatSchema>;

const doorToDoorSummarySchema = z
  .object({
    count: int,
    medianDays: days.nullable(),
    p90Days: days.nullable(),
    avgDays: days.nullable(),
  })
  .strict();

export const speedSchema = z
  .object({
    /** Shipments delivered in the period. */
    delivered: int,
    doorToDoor: doorToDoorSummarySchema,
    byRoute: z.array(d2dStatSchema).max(60),
    byService: z.array(d2dStatSchema).max(5),
    byRegion: z.array(d2dStatSchema).max(60),
    onTime: z
      .object({
        total: int,
        onTime: int,
        pct: pct.nullable(),
        byRoute: z.array(onTimeStatSchema).max(60),
        byPartner: z.array(onTimeStatSchema).max(60),
        byCity: z.array(onTimeStatSchema).max(100),
      })
      .strict(),
    legs: z.array(routeLegsSchema).max(60),
    boxesByStage: z.object({ total: int, stages: z.array(stageStatSchema) }).strict(),
  })
  .strict();
export type Speed = z.infer<typeof speedSchema>;

export const moneyTotalsSchema = z
  .object({
    /** Freight + add-ons + empty boxes, less discounts, before VAT (accounts 4000, 4010, 4100, 4800). */
    revenue: amountSchema,
    /** Other income (4900). */
    otherIncome: amountSchema,
    /** Cargo costs accrued: accounts 5010-5090 (kind cargo), net of credits. */
    cargoCosts: amountSchema,
    /** Revenue less cargo costs. */
    cargoMargin: amountSchema,
    /** Running costs: 6010-6300, plus FX gain/loss (5200). */
    runningCosts: amountSchema,
    /** Cargo margin plus other income less running costs. */
    result: amountSchema,
  })
  .strict();
export type MoneyTotals = z.infer<typeof moneyTotalsSchema>;

/**
 * One branch's result. Its amounts are in the currency of the branch's own country (`entityId`; see
 * `byEntity[].currency`) with `aedMinor` beside them, whatever the scope of the document.
 */
export const branchResultSchema = z
  .object({
    branchId: z.string(),
    name: z.string(),
    entityId: z.string(),
    revenue: amountSchema,
    cargoMargin: amountSchema,
    runningCosts: amountSchema,
    result: amountSchema,
  })
  .strict();
export type BranchResult = z.infer<typeof branchResultSchema>;

export const entityResultSchema = z
  .object({
    entityId: z.string(),
    name: z.string(),
    currency: z.string(),
    /** 1 unit of `currency` in AED (decimal text), null when no rate converts it. */
    aedRate: z.string().nullable(),
    totals: moneyTotalsSchema,
  })
  .strict();
export type EntityResult = z.infer<typeof entityResultSchema>;

/** One container's economics in AED: revenue of its boxes against the costs accrued for it. */
export const containerEconomicsSchema = z
  .object({
    containerId: z.string(),
    containerNo: z.string(),
    route: z.string(),
    status: z.string(),
    boxes: int,
    plannedBoxes: int.nullable(),
    /** boxes / planned boxes, whole percent; null without a cost model. */
    fillPct: int.nullable(),
    revenueAedMinor: sint.nullable(),
    /** The whole journey: port to port plus the last mile (forwarding and delivery) of its boxes. */
    costsAedMinor: sint.nullable(),
    /** Revenue less the whole journey: the main figure. */
    marginAedMinor: sint.nullable(),
    marginPerBoxAedMinor: sint.nullable(),
    costPerBoxAedMinor: sint.nullable(),
    /** Port to port only: the container's own accruals and bills. */
    portCostsAedMinor: sint.nullable().default(null),
    portMarginAedMinor: sint.nullable().default(null),
    portCostPerBoxAedMinor: sint.nullable().default(null),
    /** Forwarding and delivery of its boxes (handover accruals, the cost model for the rest). */
    lastMileAedMinor: sint.nullable().default(null),
    /** What the cost model said one box would cost on this container, port to port, in AED. */
    estimatePerBoxAedMinor: sint.nullable(),
    /** Actual against estimate per box, % (positive = dearer than estimated). */
    variancePct: z.number().nullable(),
    /** Boxes at which this container's revenue per box covers the whole journey. */
    breakEvenBoxes: int.nullable(),
    /** The container has been devanned: no further costs are expected from the accruals counted here. */
    costsComplete: z.boolean(),
  })
  .strict();
export type ContainerEconomics = z.infer<typeof containerEconomicsSchema>;

export const moneySchema = z
  .object({
    /** Currency of every `minor`: the scope's own; AED for the group. */
    currency: z.string(),
    totals: moneyTotalsSchema,
    /** Boxes collected in the period, and the average per box. */
    perBox: z
      .object({
        boxes: int,
        revenue: amountSchema.nullable(),
        cargoCosts: amountSchema.nullable(),
        cargoMargin: amountSchema.nullable(),
      })
      .strict(),
    byBranch: z.array(branchResultSchema).max(300),
    /** One row per country with its own books; the group's `totals` are their AED sum. */
    byEntity: z.array(entityResultSchema).max(60),
    containers: z.array(containerEconomicsSchema).max(25),
    costAccuracy: z
      .object({
        containers: int,
        actualPerBoxAedMinor: sint.nullable(),
        estimatePerBoxAedMinor: sint.nullable(),
        variancePct: z.number().nullable(),
      })
      .strict(),
    breakEven: z.object({ avgBoxes: int.nullable(), avgFillPct: z.number().nullable() }).strict(),
    /** Today's position (not period-bound). */
    cash: z
      .object({
        /** Safes and banks. */
        cash: amountSchema,
        /** Reserves held for boxes already sold (allocated less released). */
        reserves: amountSchema.nullable(),
        /** Cash less reserves held. */
        freeCash: amountSchema,
        /** Reserves held / required by the cash calendar, whole percent; null when nothing is required. */
        reservesCoveragePct: int.nullable(),
      })
      .strict(),
    /** The next 30 days: payments due (cash calendar, unpaid bills, advances) against receipts expected. */
    next30Days: z
      .object({
        payments: amountSchema.nullable(),
        expectedReceipts: amountSchema,
        net: amountSchema.nullable(),
        overdue: amountSchema.nullable(),
      })
      .strict(),
    receivables: z.object({ amount: amountSchema, shipments: int }).strict(),
    /** Entities (or the entity) left out of AED sums for want of an FX rate. */
    missingFx: z.array(z.string()),
  })
  .strict();
export type Money = z.infer<typeof moneySchema>;

export const campaignResultSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    channel: z.string(),
    newCustomers: int,
    spend: amountSchema.nullable(),
    costPerNewCustomer: amountSchema.nullable(),
  })
  .strict();
export type CampaignResult = z.infer<typeof campaignResultSchema>;

export const topCustomerSchema = z
  .object({
    customerId: z.string(),
    name: z.string(),
    shipments: int,
    boxes: int,
    revenue: amountSchema,
  })
  .strict();
export type TopCustomer = z.infer<typeof topCustomerSchema>;

export const customersSchema = z
  .object({
    /** First shipment in the period. */
    newCustomers: int,
    /** Customers who shipped in the period, and how many had shipped before. */
    repeat: z.object({ customers: int, repeat: int, pct: pct.nullable() }).strict(),
    /** Shipped in the last 6 months, and shipped before but not in the last 6 months (today's counts). */
    active6m: int,
    lapsed6m: int,
    acquisition: z.array(z.object({ source: z.string(), count: int }).strict()).max(10),
    campaigns: z.array(campaignResultSchema).max(30),
    /** Campaign spend in the period / new customers that came through campaigns; null without either. */
    costPerNewCustomer: amountSchema.nullable(),
    referrals: z.object({ signups: int, shipments: int }).strict(),
    satisfaction: z
      .object({
        ratings: int,
        /** Average stars, 1-5; null without ratings. */
        csat: z.number().min(0).max(5).nullable(),
        /** Net score: share of 4-5 stars less share of 1-3 stars, as in `feedbackScores` (-100..100). */
        nps: z.number().min(-100).max(100).nullable(),
        distribution: z.tuple([int, int, int, int, int]),
        openComplaints: int,
        oldestComplaintDays: days.nullable(),
      })
      .strict(),
    topCustomers: z.array(topCustomerSchema).max(10),
  })
  .strict();
export type Customers = z.infer<typeof customersSchema>;

export const exceptionTypeStatSchema = z
  .object({
    type: z.string(),
    count: int,
    avgAgeHours: days,
    oldestAgeHours: days,
  })
  .strict();

export const bankReconSchema = z
  .object({
    accounts: int,
    reconciled: int,
    unsortedLines: int,
    lastReconciledAt: iso.nullable(),
  })
  .strict();
export type BankRecon = z.infer<typeof bankReconSchema>;

export const healthSchema = z
  .object({
    exceptions: z
      .object({
        open: int,
        byType: z.array(exceptionTypeStatSchema).max(20),
        /** Open exceptions by age: under 4 h, 4-24 h, 1-3 days, over 3 days. */
        byAge: z.object({ under4h: int, h4to24: int, d1to3: int, over3d: int }).strict(),
      })
      .strict(),
    /** Boxes the rates below are measured against: boxes collected in the period. */
    boxesCollected: int,
    missing: int,
    damaged: int,
    missingPer1000: z.number().nonnegative().nullable(),
    damagedPer1000: z.number().nonnegative().nullable(),
    pickups: z.object({ completed: int, onTime: int, pct: pct.nullable(), open: int }).strict(),
    /** Day closes done in the period against branch-days that should have closed. */
    dayCloses: z.object({ done: int, expected: int, pct: pct.nullable() }).strict(),
    /** From the finance agent's bank reconciliation status; null until it exists. */
    bankReconciliation: bankReconSchema.nullable(),
  })
  .strict();
export type Health = z.infer<typeof healthSchema>;

/** Row 1 counts that are cheap to keep here (the full list is `snapshots/operations`). */
export const attentionSchema = z
  .object({
    openAlerts: int,
    breachedAlerts: int,
    approvalsWaiting: int,
    /** Tagged shipments predicted to miss a season's delivery date (late or at risk). */
    seasonAtRisk: int,
  })
  .strict();

export const COMPARISON_METRICS = [
  'delivered',
  'medianDays',
  'p90Days',
  'onTimePct',
  'revenue',
  'cargoMargin',
  'runningCosts',
  'result',
  'newCustomers',
  'repeatPct',
  'csat',
  'nps',
  'missingPer1000',
  'damagedPer1000',
  'pickupsOnTimePct',
  'dayClosesPct',
] as const;
export type ComparisonMetric = (typeof COMPARISON_METRICS)[number];

export const comparisonMetricSchema = z
  .object({
    current: z.number().nullable(),
    previous: z.number().nullable(),
    /** current - previous; null when either is missing. */
    change: z.number().nullable(),
    /** change / |previous|, in percent; null when previous is missing or 0. */
    changePct: z.number().nullable(),
  })
  .strict();
export type ComparisonValue = z.infer<typeof comparisonMetricSchema>;

export const comparisonSchema = z
  .object({
    previousRange: z.object({ from: date, to: date }).strict(),
    /** Money metrics are in AED minor units (revenue, cargoMargin, runningCosts, result). */
    metrics: z.record(z.enum(COMPARISON_METRICS), comparisonMetricSchema),
  })
  .strict();
export type Comparison = z.infer<typeof comparisonSchema>;

export const dashboardLimitsSchema = z
  .object({
    legsRead: int,
    legsCap: int,
    shipmentsRead: int,
    shipmentsCap: int,
    journalsRead: int,
    journalsCap: int,
    boxesRead: int,
    boxesCap: int,
    /** True when any cap was reached: the numbers are an undercount. */
    truncated: z.boolean(),
  })
  .strict();
export type DashboardLimits = z.infer<typeof dashboardLimitsSchema>;

export const DASHBOARD_VERSION = 1;
/** One dashboard document stays far below Firestore's 1 MiB; the job refuses (never cuts) above this. */
export const DASHBOARD_MAX_BYTES = 500 * 1024;

/** `dashboards/{period}__{scope}` */
export const dashboardDocSchema = z
  .object({
    version: z.literal(DASHBOARD_VERSION),
    id: z.string(),
    period: z.enum(DASHBOARD_PERIODS),
    scopeKind: z.enum(DASHBOARD_SCOPE_KINDS),
    /** `group`, a country code, or a branch code: what the rules compare with the reader's access. */
    scopeId: z.string(),
    scopeName: z.string(),
    /** The country of an entity or branch scope; null for the group. */
    entityId: z.string().nullable(),
    /** The scope's currency (AED for the group) and time zone used to cut the period. */
    currency: z.string(),
    timezone: z.string(),
    generatedAt: iso,
    durationMs: int,
    source: z.enum(['schedule', 'dev']),
    /** The period: business dates, both inclusive. */
    range: z.object({ from: date, to: date }).strict(),
    speed: speedSchema,
    money: moneySchema,
    customers: customersSchema,
    health: healthSchema,
    attention: attentionSchema,
    comparison: comparisonSchema,
    limits: dashboardLimitsSchema,
  })
  .strict();
export type DashboardDoc = z.infer<typeof dashboardDocSchema>;

// ---- Season board ---------------------------------------------------------------------------

export const seasonShipmentSchema = z
  .object({
    trackingNo: z.string(),
    stage: z.enum(PREDICTION_STAGES),
    route: z.string(),
    service: z.enum(['sea', 'air', 'prime']),
    cityName: z.string(),
    risk: z.enum(SEASON_RISKS),
    /** The predicted window's median and late end, and days the median is past the date (0 when not past). */
    predictedMedian: date,
    predictedTo: date,
    daysOver: int,
    basis: z.enum(['history', 'target']),
    /** What would save it, in plain words. */
    action: z.string(),
  })
  .strict();
export type SeasonShipment = z.infer<typeof seasonShipmentSchema>;

/** `seasonBoards/{seasonId}`: shipments of a season tag against its deliver-by date. */
export const seasonBoardSchema = z
  .object({
    version: z.literal(1),
    seasonId: z.string(),
    name: z.string(),
    colour: z.string(),
    deliverBy: date,
    daysLeft: z.number().int(),
    generatedAt: iso,
    totals: z
      .object({
        tagged: int,
        delivered: int,
        onTrack: int,
        atRisk: int,
        late: int,
        /** Tagged and delivered on or before the date / after it. */
        deliveredInTime: int,
        deliveredLate: int,
      })
      .strict(),
    /** On track / at risk / late by where the shipments are (their last milestone). */
    byStage: z
      .array(
        z
          .object({
            stage: z.enum(PREDICTION_STAGES),
            onTrack: int,
            atRisk: int,
            late: int,
          })
          .strict(),
      )
      .max(10),
    /** The collection cut-offs by country and service with days left. */
    cutoffs: z
      .array(
        z
          .object({
            originSectorId: z.string(),
            service: z.enum(['sea', 'air', 'prime']),
            lastCollectDate: date,
            lastLoadDate: date.nullable(),
            daysLeft: z.number().int(),
          })
          .strict(),
      )
      .max(40),
    /** The shipments at risk or late, latest prediction first (at most 100). */
    atRisk: z.array(seasonShipmentSchema).max(100),
    truncated: z.boolean(),
  })
  .strict();
export type SeasonBoard = z.infer<typeof seasonBoardSchema>;
